import { FC, useMemo } from "react";
import { Button, IconButton } from "@hostingbv/lib/src/components/form/Button";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { FilterableDataTable, navigateRow } from "@ventumsoftware/react-toolkit";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { useApiClient } from "@hostingbv/lib/src/api";
import { config } from "../config";
import { useNavigate } from "react-router-dom";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { DeploymentStatusLabel } from "@hostingbv/lib/src/components/DeploymentStatusLabel";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { EntityNotesButton } from "../components/EntityNotesButton";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { useRefreshEffect } from "../../../lib/src/components/RefreshController";
import { MailDomain } from "../../../lib/src/api/partner/models/MailDomain";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";
import { faAdd, faEnvelopeOpenText, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { useModal } from "../../../lib/src/components/layout/ModalProvider";
import { AddMailDomainModal } from "../modals/AddMailDomainModal";
import { TabLayout } from "@hostingbv/lib/src/components/content/TabLayout";
import { usePersistentState } from "@ventumsoftware/react-toolkit";

export const MailDomains: FC = () => {
  const { partnerClient } = useApiClient(config);
  const {
    resource,
    loading: loadingMailDomains,
    reload: reloadMailDomains,
  } = useFetchedResource(() => partnerClient.getAllMailDomains());
  const { can } = usePermissions(partnerClient);
  const reload = () => {
    reloadMailDomains();
  };
  useRefreshEffect(() => {
    reload();
  });
  const mailDomains: MailDomain[] = useMemo(() => {
    if (resource?.success === true && resource.data) {
      return resource.data;
    }
    return [];
  }, [resource]);
  return (
    <ContentContainer size={"lg"}>
      <PageIconHeader icon={faEnvelopeOpenText}>
        <Breadcrumbs crumbs={[]} currentPage={"Mail domains"} />
        <PageHeader>Mail Domains</PageHeader>
      </PageIconHeader>
      {can("create", "mail") && <AddMailDomainForm />}
      <MailDomainOverview mailDomains={mailDomains} loading={loadingMailDomains} reloadMailDomains={reload} />
    </ContentContainer>
  );
};

const AddMailDomainForm: FC = () => {
  const { open: openAddMailDomainModal } = useModal({ title: "Add mail domain", body: <AddMailDomainModal /> });
  return (
    <Callout>
      <SectionHeader>Deploy a new mail domain</SectionHeader>
      <Paragraph>You can then add mail users, aliases and forwards to that domain.</Paragraph>
      <Button
        size={"sm"}
        type={"primary"}
        text={"Deploy new mail domain"}
        icon={faAdd}
        onClick={() => openAddMailDomainModal()}
      />
    </Callout>
  );
};

const MAIL_TYPE_LABELS: Record<string, string> = {
  imap_mail_domain: "IMAP",
  transactional_mail_domain: "Transactional",
  ms_mail_domain: "Microsoft 365",
};

const MailDomainOverview: FC<{ mailDomains: MailDomain[]; loading: boolean; reloadMailDomains: () => void }> = (
  props,
) => {
  const navigate = useNavigate();
  const [mailTypeFilter, setMailTypeFilter] = usePersistentState<string>("mail.filter", "All");
  const mailDomains = useMemo(() => {
    const typeMap: Record<string, string> = { IMAP: "imap_mail_domain", Transactional: "transactional_mail_domain" };
    const targetType = typeMap[mailTypeFilter];
    return props.mailDomains.filter((mailDomain) => !targetType || mailDomain.mailType === targetType);
  }, [props.mailDomains, mailTypeFilter]);
  const { getRealtimeStatus } = useRealtimeDeployments()!;
  return (
    <div className={"my-8"}>
      <SectionHeader>Your mail domains</SectionHeader>
      <TabLayout tabNames={["All", "IMAP", "Transactional"]} activeTab={mailTypeFilter} setActiveTab={setMailTypeFilter} />
      <FilterableDataTable
        keyProperty={"id"}
        data={mailDomains}
        searchableProperties={["displayName", (mailDomain) => mailDomain.customer?.displayName, "notes"]}
        actions={<IconButton icon={faRefresh} onClick={props.reloadMailDomains} type={"secondary"} size={"sm"} />}
        loading={props.loading}
        columns={[
          {
            header: "Name",
            property: "displayName",
            transform: (name, row) => (
              <div className={"flex items-center space-x-2"}>
                <span>{name}</span>
                <EntityNotesButton allowNew={false} entity={row} />
              </div>
            ),
            sort: true,
          },
          {
            header: "Customer",
            property: "customer",
            transform: (customer) => customer?.displayName ?? "n/a",
            sort: true,
          },
          {
            header: "Type",
            property: "mailType",
            transform: (mailType) => MAIL_TYPE_LABELS[mailType] ?? mailType,
          },
          {
            header: "Deployment status",
            property: "deploymentStatus",
            transform: (status, row) => <DeploymentStatusLabel status={getRealtimeStatus(row.id, status)} />,
          },
        ]}
        onClickRow={(row, e) => navigateRow(navigate, `/mail/${row.id}`, e)}
      />
    </div>
  );
};
